/**
 * Instanced resource-node rendering.
 *
 * Resource nodes (trees / rocks / gold / berries) are STATIC and numerous
 * (thousands on a large map). Rendering each as its own THREE.Group with a
 * unique geometry+material — as a naive port did — produced ~6k meshes and
 * ~3.6k GPU geometries, which tanked the frame rate even though only a handful
 * are ever on screen (three.js still walks every object each frame).
 *
 * This layer renders ALL nodes of a kind as one (or two) InstancedMesh — a
 * couple of draw calls total, regardless of count, exactly like the standalone
 * demo. Nodes never spawn after match start; they only deplete, so we build the
 * pools once and simply hide an instance (zero scale) when its entity dies.
 *
 * Picking: each kind has a "body" InstancedMesh whose instance index maps 1:1
 * to a node. pickEid raycasts these and resolves intersection.instanceId → eid.
 */
import * as THREE from "three";
import type { World } from "../sim/world";
import type { Eid } from "../sim/types";
import type { Biome } from "../sim/hex";
import type { Fog } from "./fog";

interface Node {
  eid: Eid;
  x: number;
  y: number;
  z: number;
  biome: Biome;
}

interface KindPool {
  /** Instance index -> eid, or -1 once hidden/depleted. Length == node count. */
  eidOf: Eid[];
  /** eid -> instance index, for O(1) hide on death. */
  indexOf: Map<Eid, number>;
  /** Meshes to add to the scene (body first; body is the pick target). */
  meshes: THREE.InstancedMesh[];
  /** Berries etc. attach extra instances per node (subCount > 1). */
  extras: { mesh: THREE.InstancedMesh; subCount: number }[];
  body: THREE.InstancedMesh;
  /** Meshes whose instance index maps 1:1 to a node — all are raycast targets
   *  so the whole visible prop (e.g. a tree's canopy) is clickable. */
  pickMeshes: THREE.InstancedMesh[];
  // ---- fog of war (lazy): pristine per-instance colours + per-mesh subCount +
  //      per-node current brightness, so applyFog only re-tints what changed.
  fogBase?: (Float32Array | null)[];
  fogSub?: number[];
  nodeBright?: Float32Array;
}

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
const _fogCol = new THREE.Color();

/** Deterministic per-eid pseudo-random in [0,1) — no Math.random (stable across frames). */
function rnd(eid: number, salt: number): number {
  const s = Math.sin(eid * 12.9898 + salt * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/** Blossom colours for flowering broadleaf trees (cherry pink, white, golden,
 *  coral, lilac) — picked per tree for spring-y variety. */
const BLOSSOMS = [0xf2a6c2, 0xf3eef2, 0xf2d24a, 0xe98a8a, 0xc9a6f0] as const;

type Archetype = "pine" | "leaf" | "palm" | "snowy" | "dead";

/** Pick a tree shape for a hex's biome (deterministic per node), so each climate
 *  reads differently: conifers in cold/temperate forest, snow-capped firs in the
 *  taiga/snow, broadleaf in grass/jungle, palms in the desert, bare snags here
 *  and there. */
function archetype(biome: Biome, eid: number): Archetype {
  const r = rnd(eid, 9);
  switch (biome) {
    case "snow": return r < 0.18 ? "dead" : "snowy";
    case "taiga": return r < 0.5 ? "snowy" : "pine";
    case "forest": return r < 0.62 ? "pine" : "leaf";
    case "jungle": return "leaf";
    case "grass": return r < 0.5 ? "leaf" : "pine";
    case "savanna": return r < 0.22 ? "dead" : "leaf";
    case "desert": return r < 0.5 ? "dead" : "palm";
    case "sand": return r < 0.45 ? "dead" : "palm";
    case "rock":
    case "mountain": return r < 0.4 ? "dead" : "pine";
    default: return "pine";
  }
}

export class ResourceLayer {
  readonly group = new THREE.Group();
  private pools = new Map<string, KindPool>();
  /** Every eid we render, for cheap per-frame death detection. */
  private live = new Set<Eid>();
  /** Fixed world position per node (props never move) — for fog look-ups even
   *  after the sim has removed the entity (last-seen memory). */
  private nodePos = new Map<Eid, { x: number; z: number }>();

  constructor(world: World, size: number) {
    const byKind = new Map<string, Node[]>();
    for (const [eid, id] of world.ident) {
      if (id.kind !== "resource") continue;
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const hex = world.hexAtWorld(tr.pos.x, tr.pos.y);
      const y = hex?.height ?? 0;
      const list = byKind.get(id.defId) ?? [];
      list.push({ eid, x: tr.pos.x, y, z: tr.pos.y, biome: hex?.biome ?? "grass" });
      byKind.set(id.defId, list);
      this.live.add(eid);
      this.nodePos.set(eid, { x: tr.pos.x, z: tr.pos.y });
    }

    for (const [defId, nodes] of byKind) {
      const pool = this.buildPool(defId, nodes, size);
      this.pools.set(defId, pool);
      // every 1:1 mesh resolves instanceId -> eid, so the canopy is clickable
      for (const m of pool.pickMeshes) m.userData.resourcePool = pool;
      this.group.add(...pool.meshes);
    }
  }

  /** InstancedMesh raycast targets (all 1-per-node meshes) for picking. */
  pickTargets(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const p of this.pools.values()) out.push(...p.pickMeshes);
    return out;
  }

  /** Resolve a raycast hit on a pool body mesh to the entity id, or null. */
  eidAt(mesh: THREE.Object3D, instanceId: number | undefined): Eid | null {
    const pool = mesh.userData?.resourcePool as KindPool | undefined;
    if (!pool || instanceId === undefined) return null;
    const eid = pool.eidOf[instanceId];
    return eid === undefined || eid < 0 ? null : eid;
  }

  /** Hide instances whose entities died this step. Cheap: a Set membership scan.
   *  Returns true when at least one node was hidden, so the caller can refresh
   *  the (otherwise frozen) shadow map — a felled tree must drop its shadow. */
  update(world: World, fog?: Fog): boolean {
    let removed = false;
    for (const eid of this.live) {
      if (world.transform.has(eid)) continue; // still alive
      // FOG MEMORY: a prop removed while its hex is out of vision stays rendered
      // (last-seen) — the player must not learn it was harvested until they
      // re-explore. Only hide it once its hex is actually visible.
      if (fog && fog.enabled) {
        const p = this.nodePos.get(eid);
        if (p && !fog.isPosVisible(world, p.x, p.z)) continue;
      }
      this.live.delete(eid);
      this.nodePos.delete(eid);
      removed = true;
      for (const pool of this.pools.values()) {
        const idx = pool.indexOf.get(eid);
        if (idx === undefined) continue;
        pool.indexOf.delete(eid);
        pool.eidOf[idx] = -1;
        pool.body.setMatrixAt(idx, ZERO);
        for (const ex of pool.extras) {
          for (let s = 0; s < ex.subCount; s++) ex.mesh.setMatrixAt(idx * ex.subCount + s, ZERO);
        }
      }
    }
    if (!removed) return false;
    for (const pool of this.pools.values()) {
      pool.body.instanceMatrix.needsUpdate = true;
      for (const ex of pool.extras) ex.mesh.instanceMatrix.needsUpdate = true;
    }
    return true;
  }

  /** Fog of war: darken resource props (trees/rocks/…) on out-of-vision hexes to
   *  `dim`, restore to full when seen. Per-instance colour tint; only re-tints
   *  nodes whose visibility changed. */
  applyFog(fog: Fog, world: World, dim: number): void {
    if (!fog.enabled) return;
    for (const pool of this.pools.values()) {
      if (!pool.fogBase) {
        pool.fogBase = pool.meshes.map((m) => (m.instanceColor ? Float32Array.from(m.instanceColor.array as Float32Array) : null));
        pool.fogSub = [1, ...pool.extras.map((e) => e.subCount)]; // body is 1:1
        pool.nodeBright = new Float32Array(pool.eidOf.length).fill(1);
      }
      const base = pool.fogBase, sub = pool.fogSub!, bright = pool.nodeBright!;
      const changed = new Array<boolean>(pool.meshes.length).fill(false);
      for (let n = 0; n < pool.eidOf.length; n++) {
        const eid = pool.eidOf[n];
        if (eid < 0) continue;
        const p = this.nodePos.get(eid); // fixed pos (works even after the sim removed it)
        if (!p) continue;
        const b = fog.isPosVisible(world, p.x, p.z) ? 1 : dim;
        if (bright[n] === b) continue;
        bright[n] = b;
        for (let mi = 0; mi < pool.meshes.length; mi++) {
          const baseCol = base[mi];
          if (!baseCol) continue;
          const sc = sub[mi];
          const mesh = pool.meshes[mi];
          for (let s = 0; s < sc; s++) {
            const o = (n * sc + s) * 3;
            if (o + 2 >= baseCol.length) continue;
            _fogCol.setRGB(baseCol[o] * b, baseCol[o + 1] * b, baseCol[o + 2] * b);
            mesh.setColorAt(n * sc + s, _fogCol);
          }
          changed[mi] = true;
        }
      }
      for (let mi = 0; mi < pool.meshes.length; mi++) {
        if (changed[mi] && pool.meshes[mi].instanceColor) pool.meshes[mi].instanceColor!.needsUpdate = true;
      }
    }
  }

  // ---- pool builders -------------------------------------------------------

  private buildPool(defId: string, nodes: Node[], u: number): KindPool {
    switch (defId) {
      case "tree": return this.buildTrees(nodes, u);
      case "stone_rock": return this.buildRocks(nodes, u);
      case "gold_ore": return this.buildGold(nodes, u);
      case "berry_bush": return this.buildBerries(nodes, u);
      case "fish_school": return this.buildFish(nodes, u);
      default: return this.buildRocks(nodes, u); // unknown node -> rock-like fallback
    }
  }

  private newPool(body: THREE.InstancedMesh, extras: { mesh: THREE.InstancedMesh; subCount: number }[], nodes: Node[]): KindPool {
    const eidOf = nodes.map((n) => n.eid);
    const indexOf = new Map<Eid, number>();
    nodes.forEach((n, i) => indexOf.set(n.eid, i));
    const meshes = [body, ...extras.map((e) => e.mesh)];
    for (const m of meshes) m.frustumCulled = false; // one pool spans the map; never cull as a whole
    // pickable = body + extras that are 1-per-node (e.g. tree foliage), so the
    // whole prop is clickable. Multi-instance extras (berries) are excluded —
    // their instance index doesn't map 1:1 to a node.
    const pickMeshes = [body, ...extras.filter((e) => e.subCount === 1).map((e) => e.mesh)];
    return { eidOf, indexOf, meshes, extras, body, pickMeshes };
  }

  /**
   * Trees come in five archetypes chosen by the hex's biome (so a desert grows
   * palms and a taiga grows snowy conifers, not the same cone everywhere). The
   * trunk is one instanced "body" mesh; each FOLIAGE style is its own instanced
   * mesh sized to every node, but a node only fills the slot for its archetype
   * and ZEROs the others — that keeps the 1-instance-per-node mapping the pool's
   * picking + depletion rely on, while rendering as a handful of draw calls.
   * Per-instance colour + scale jitter keep the canopy lively.
   */
  private buildTrees(nodes: Node[], u: number): KindPool {
    const mat = (c: number) => new THREE.MeshStandardMaterial({ color: c, roughness: 1, flatShading: true });
    const trunkGeo = new THREE.CylinderGeometry(0.05 * u, 0.075 * u, 0.34 * u, 5);
    const pineGeo = new THREE.ConeGeometry(0.3 * u, 0.8 * u, 6); pineGeo.translate(0, 0.2 * u, 0);
    const leafGeo = new THREE.IcosahedronGeometry(0.33 * u, 0); // round broadleaf
    const palmGeo = new THREE.ConeGeometry(0.42 * u, 0.2 * u, 6); palmGeo.translate(0, 0.02 * u, 0); // umbrella crown
    const snowGeo = new THREE.ConeGeometry(0.2 * u, 0.34 * u, 6); snowGeo.translate(0, 0.12 * u, 0); // snow cap on a conifer
    const flowerGeo = new THREE.IcosahedronGeometry(0.055 * u, 0); // blossom cluster speck

    const N = nodes.length;
    const FLW = 4; // blossoms per flowering broadleaf
    const trunk = new THREE.InstancedMesh(trunkGeo, mat(0xffffff), N);
    const pine = new THREE.InstancedMesh(pineGeo, mat(0xffffff), N);
    const leaf = new THREE.InstancedMesh(leafGeo, mat(0xffffff), N);
    const palm = new THREE.InstancedMesh(palmGeo, mat(0xffffff), N);
    const snow = new THREE.InstancedMesh(snowGeo, mat(0xffffff), N);
    const flower = new THREE.InstancedMesh(flowerGeo, mat(0xffffff), N * FLW);
    for (const m of [trunk, pine, leaf, palm, snow]) m.castShadow = true;

    const d = new THREE.Object3D();
    const col = new THREE.Color();
    const col2 = new THREE.Color();
    const ZEROc = (m: THREE.InstancedMesh, i: number) => { m.setMatrixAt(i, ZERO); };
    // per-instance colour = base × brightness jitter (lively, non-uniform canopy)
    const tint = (base: number, eid: number, lo = 0.82, hi = 1.16): THREE.Color =>
      col.setHex(base).multiplyScalar(lo + rnd(eid, 7) * (hi - lo));

    nodes.forEach((n, i) => {
      const a = archetype(n.biome, n.eid);
      const s = 0.7 + rnd(n.eid, 1) * 0.5;
      // node position already includes the organic scatter (set in spawn), so the
      // trunk sits exactly on its collision footprint — no extra offset here.
      const x = n.x;
      const z = n.z;
      const yaw = rnd(n.eid, 4) * Math.PI * 2;

      // --- trunk (always) --- palms are tall + slim, the rest standard
      const tall = a === "palm" ? 1.7 : a === "leaf" && n.biome === "jungle" ? 1.5 : 1;
      d.position.set(x, n.y + 0.17 * u * s * tall, z);
      d.rotation.set(0, yaw, 0);
      d.scale.set(s * (a === "palm" ? 0.6 : 1), s * tall, s * (a === "palm" ? 0.6 : 1));
      d.updateMatrix();
      trunk.setMatrixAt(i, d.matrix);
      trunk.setColorAt(i, tint(a === "palm" ? 0x9a6b3a : a === "dead" ? 0x6e6256 : 0x6b4a2b, n.eid, 0.9, 1.1));

      // Crown height must track the TRUNK TOP, which rises 0.34·u·s·tall. The old
      // coupling rose 0.65 per `tall`, so tall (jungle) trees' crowns outran their
      // trunks and floated. Rising 0.34·tall (+ const) keeps the same snug overlap
      // for every height; const 0.11 leaves tall=1 trees pixel-identical (→0.45).
      const crownY = n.y + (0.34 * tall + 0.11) * u * s;
      // --- foliage: fill the chosen archetype, zero the rest ---
      const usePine = a === "pine" || a === "snowy";
      if (usePine) {
        d.position.set(x, crownY, z); d.rotation.set(0, yaw, 0); d.scale.setScalar(s);
        d.updateMatrix(); pine.setMatrixAt(i, d.matrix);
        const green = n.biome === "taiga" || n.biome === "snow" ? 0x3a6b4a : 0x3f7a32;
        pine.setColorAt(i, tint(green, n.eid));
      } else ZEROc(pine, i);

      // broadleaf canopy — some are in bloom (a blossom-tinted crown + petal
      // specks), for colour amid the green. Jungle stays lush green, no blooms.
      const flowering = a === "leaf" && n.biome !== "jungle" && rnd(n.eid, 8) < 0.4;
      const blossom = BLOSSOMS[Math.floor(rnd(n.eid, 11) * BLOSSOMS.length)];
      const canopyY = crownY + 0.05 * u * s;
      if (a === "leaf") {
        d.position.set(x, canopyY, z); d.rotation.set(0, yaw, 0); d.scale.set(s * 1.05, s * (n.biome === "savanna" ? 0.7 : 1), s * 1.05);
        d.updateMatrix(); leaf.setMatrixAt(i, d.matrix);
        const green = n.biome === "jungle" ? 0x2f6b24 : n.biome === "savanna" ? 0x7e8a3c : 0x5a9a3e;
        // a flowering crown leans toward its blossom colour (still a little green)
        if (flowering) leaf.setColorAt(i, col.setHex(blossom).lerp(col2.setHex(green), 0.3).multiplyScalar(0.92 + rnd(n.eid, 7) * 0.16));
        else leaf.setColorAt(i, tint(green, n.eid));
      } else ZEROc(leaf, i);
      // petal specks dotted over a flowering crown; zeroed otherwise
      for (let f = 0; f < FLW; f++) {
        const fi = i * FLW + f;
        if (!flowering) { flower.setMatrixAt(fi, ZERO); continue; }
        const fa = rnd(n.eid, 80 + f) * Math.PI * 2;
        const fr = (0.12 + rnd(n.eid, 90 + f) * 0.2) * u * s;
        d.position.set(x + Math.cos(fa) * fr, canopyY + (rnd(n.eid, 100 + f) - 0.4) * 0.18 * u * s, z + Math.sin(fa) * fr);
        d.rotation.set(0, 0, 0); d.scale.setScalar(s * (0.7 + rnd(n.eid, 110 + f) * 0.6));
        d.updateMatrix(); flower.setMatrixAt(fi, d.matrix);
        flower.setColorAt(fi, col.setHex(blossom).multiplyScalar(0.95 + rnd(n.eid, 120 + f) * 0.2));
      }

      if (a === "palm") {
        // crown sits ON the trunk top (trunk spans 0..0.34·u·s·tall), not floating above it
        d.position.set(x, n.y + 0.33 * u * s * tall, z); d.rotation.set(0, yaw, 0); d.scale.setScalar(s);
        d.updateMatrix(); palm.setMatrixAt(i, d.matrix);
        palm.setColorAt(i, tint(0x6fae46, n.eid));
      } else ZEROc(palm, i);

      if (a === "snowy") {
        d.position.set(x, crownY + 0.16 * u * s, z); d.rotation.set(0, yaw, 0); d.scale.setScalar(s);
        d.updateMatrix(); snow.setMatrixAt(i, d.matrix);
        snow.setColorAt(i, tint(0xeef3f7, n.eid, 0.92, 1.0));
      } else ZEROc(snow, i);
    });
    for (const m of [trunk, pine, leaf, palm, snow, flower]) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
    return this.newPool(trunk, [
      { mesh: pine, subCount: 1 }, { mesh: leaf, subCount: 1 },
      { mesh: palm, subCount: 1 }, { mesh: snow, subCount: 1 },
      { mesh: flower, subCount: FLW },
    ], nodes);
  }

  /** Stone: a main boulder plus a couple of scattered pebbles, with squat/tall
   *  scale and grey-tone jitter per instance so a stone field reads as varied
   *  rubble instead of identical clones. */
  private buildRocks(nodes: Node[], u: number): KindPool {
    const matOf = () => new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true });
    const SUB = 2;
    const main = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.24 * u, 0), matOf(), nodes.length);
    const peb = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.1 * u, 0), matOf(), nodes.length * SUB);
    main.castShadow = true; main.receiveShadow = true; peb.castShadow = true;
    const d = new THREE.Object3D();
    const col = new THREE.Color();
    const grey = (eid: number, salt: number) => col.setHex(0x8b8579).multiplyScalar(0.66 + rnd(eid, salt) * 0.58);
    nodes.forEach((n, i) => {
      const s = 0.5 + rnd(n.eid, 1) * 0.8;
      const flat = 0.55 + rnd(n.eid, 6) * 0.85; // squat ↔ tall boulders
      d.position.set(n.x, n.y + 0.1 * u * s, n.z);
      d.rotation.set(rnd(n.eid, 2) * 0.6, rnd(n.eid, 3) * Math.PI * 2, rnd(n.eid, 4) * 0.6);
      d.scale.set(s, s * flat, s);
      d.updateMatrix(); main.setMatrixAt(i, d.matrix);
      main.setColorAt(i, grey(n.eid, 5));
      for (let p = 0; p < SUB; p++) {
        const a = rnd(n.eid, 10 + p) * Math.PI * 2;
        const rr = (0.16 + rnd(n.eid, 20 + p) * 0.18) * u * s;
        d.position.set(n.x + Math.cos(a) * rr, n.y + 0.04 * u * s, n.z + Math.sin(a) * rr);
        d.rotation.set(rnd(n.eid, 30 + p) * 3, rnd(n.eid, 40 + p) * 3, rnd(n.eid, 50 + p) * 3);
        d.scale.setScalar(s * (0.45 + rnd(n.eid, 60 + p) * 0.6));
        d.updateMatrix(); peb.setMatrixAt(i * SUB + p, d.matrix);
        peb.setColorAt(i * SUB + p, grey(n.eid, 70 + p));
      }
    });
    for (const m of [main, peb]) { m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true; }
    return this.newPool(main, [{ mesh: peb, subCount: SUB }], nodes);
  }

  /** Gold: a dull host BOULDER studded with a varying number of bright metallic
   *  nuggets at random spots on its surface — an ore vein, not a floating gem.
   *  Nugget count/size/placement and rock tone all jitter per node. */
  private buildGold(nodes: Node[], u: number): KindPool {
    const rockMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true });
    const goldMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3, metalness: 0.75, flatShading: true, emissive: 0x4a3600 });
    const SUB = 8; // plenty of nuggets so a gold vein is obviously gold, not grey rock
    const rock = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.2 * u, 0), rockMat, nodes.length);
    const gold = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.075 * u, 0), goldMat, nodes.length * SUB);
    rock.castShadow = true; rock.receiveShadow = true;
    const d = new THREE.Object3D();
    const col = new THREE.Color();
    nodes.forEach((n, i) => {
      const s = 0.6 + rnd(n.eid, 1) * 0.7;
      const flat = 0.7 + rnd(n.eid, 6) * 0.6;
      d.position.set(n.x, n.y + 0.12 * u * s, n.z);
      d.rotation.set(rnd(n.eid, 2) * 0.6, rnd(n.eid, 3) * Math.PI * 2, rnd(n.eid, 4) * 0.6);
      d.scale.set(s, s * flat, s);
      d.updateMatrix(); rock.setMatrixAt(i, d.matrix);
      rock.setColorAt(i, col.setHex(0x7a7165).multiplyScalar(0.78 + rnd(n.eid, 5) * 0.4));
      const nuggets = 5 + Math.floor(rnd(n.eid, 7) * (SUB - 4)); // 5..SUB — richly gold
      for (let g = 0; g < SUB; g++) {
        if (g >= nuggets) { gold.setMatrixAt(i * SUB + g, ZERO); continue; }
        const a = rnd(n.eid, 10 + g) * Math.PI * 2;
        const rr = rnd(n.eid, 20 + g) * 0.18 * u * s;
        const yy = n.y + (0.05 + rnd(n.eid, 30 + g) * 0.24) * u * s;
        d.position.set(n.x + Math.cos(a) * rr, yy, n.z + Math.sin(a) * rr);
        d.rotation.set(rnd(n.eid, 40 + g) * 3, rnd(n.eid, 50 + g) * 3, 0);
        d.scale.setScalar(s * (0.7 + rnd(n.eid, 60 + g) * 0.8));
        d.updateMatrix(); gold.setMatrixAt(i * SUB + g, d.matrix);
        gold.setColorAt(i * SUB + g, col.setHex(0xe6b53e).multiplyScalar(0.85 + rnd(n.eid, 70 + g) * 0.4));
      }
    });
    for (const m of [rock, gold]) { m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true; }
    return this.newPool(rock, [{ mesh: gold, subCount: SUB }], nodes);
  }

  private buildBerries(nodes: Node[], u: number): KindPool {
    const leafGeo = new THREE.IcosahedronGeometry(0.18 * u, 0);
    const leafMat = new THREE.MeshStandardMaterial({ color: 0x3d6b2a, roughness: 1, flatShading: true });
    const berryGeo = new THREE.IcosahedronGeometry(0.05 * u, 0);
    const berryMat = new THREE.MeshStandardMaterial({ color: 0xb33a2a, roughness: 0.7, flatShading: true });

    const leaf = new THREE.InstancedMesh(leafGeo, leafMat, nodes.length);
    const berries = new THREE.InstancedMesh(berryGeo, berryMat, nodes.length * 4);
    const d = new THREE.Object3D();
    nodes.forEach((n, i) => {
      const s = 0.7 + rnd(n.eid, 1) * 0.5;
      d.position.set(n.x, n.y + 0.12 * u, n.z);
      d.rotation.set(rnd(n.eid, 2), rnd(n.eid, 3) * 3, rnd(n.eid, 4));
      d.scale.setScalar(s);
      d.updateMatrix();
      leaf.setMatrixAt(i, d.matrix);
      for (let b = 0; b < 4; b++) {
        const a = (b / 4) * Math.PI * 2;
        d.position.set(n.x + Math.cos(a) * 0.1 * u, n.y + 0.16 * u, n.z + Math.sin(a) * 0.1 * u);
        d.rotation.set(0, 0, 0);
        d.scale.setScalar(s);
        d.updateMatrix();
        berries.setMatrixAt(i * 4 + b, d.matrix);
      }
    });
    leaf.instanceMatrix.needsUpdate = true;
    berries.instanceMatrix.needsUpdate = true;
    return this.newPool(leaf, [{ mesh: berries, subCount: 4 }], nodes);
  }

  /** Fish school: a cluster of little silvery fish-backs breaking the WATER
   *  surface. Unlike land props, the node's y is the sea FLOOR, so every instance
   *  is forced to the water level (resourceLayer can't import renderer3d, so the
   *  constant is duplicated). Per-instance colour lets applyFog dim it like trees. */
  private buildFish(nodes: Node[], u: number): KindPool {
    const WATER_LEVEL = 0.16; // must match renderer3d.WATER_LEVEL
    const SUB = 3; // a few extra fish per school
    const mat = () => new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5, metalness: 0.25, flatShading: true });
    const bodyGeo = new THREE.IcosahedronGeometry(0.08 * u, 0);
    const finGeo = new THREE.ConeGeometry(0.05 * u, 0.13 * u, 3);
    const body = new THREE.InstancedMesh(bodyGeo, mat(), nodes.length);
    const school = new THREE.InstancedMesh(finGeo, mat(), nodes.length * SUB);
    const d = new THREE.Object3D();
    const col = new THREE.Color();
    const silver = (eid: number, salt: number) => col.setHex(0x8fbdd8).multiplyScalar(0.78 + rnd(eid, salt) * 0.44);
    const y = WATER_LEVEL + 0.015;
    nodes.forEach((n, i) => {
      d.position.set(n.x, y, n.z);
      d.rotation.set(0, rnd(n.eid, 4) * Math.PI * 2, 0);
      d.scale.set(1.5, 0.45, 0.85); // flatten into a fish back
      d.updateMatrix(); body.setMatrixAt(i, d.matrix);
      body.setColorAt(i, silver(n.eid, 5));
      for (let s = 0; s < SUB; s++) {
        const a = rnd(n.eid, 10 + s) * Math.PI * 2;
        const rr = (0.1 + rnd(n.eid, 20 + s) * 0.22) * u;
        d.position.set(n.x + Math.cos(a) * rr, y - 0.005, n.z + Math.sin(a) * rr);
        d.rotation.set(0, a, 0);
        d.scale.set(1.1 * (0.6 + rnd(n.eid, 30 + s) * 0.6), 0.4, 0.6);
        d.updateMatrix(); school.setMatrixAt(i * SUB + s, d.matrix);
        school.setColorAt(i * SUB + s, silver(n.eid, 40 + s));
      }
    });
    for (const m of [body, school]) { m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true; }
    return this.newPool(body, [{ mesh: school, subCount: SUB }], nodes);
  }
}
